import { Icon } from '@/components/Icons';
import { TIconName } from '@/design-tokens/icons';
import { cn } from '@/utils/cn';
import React from 'react';

export interface PopoverLinkProps {
  disabled?: boolean;
  external?: boolean;
  href: string;
  icon?: string;
  label: string;
}

/**
 * Shared link renderer used by PageHeader popover content sections.
 */
export const PopoverLink: React.FC<PopoverLinkProps> = ({
  disabled,
  external,
  href,
  icon,
  label,
}) => {
  const linkClassNames = cn(
    'flex items-center justify-start gap-1',
    'focus-visible:outline-1',
    'focus-visible:outline-brand-primary-700',
    'focus-visible:outline-dashed',
    'focus-visible:outline-offset-4',
    'focus-visible:rounded-sm',
    'focus-visible:text-brand-primary',
    'pointer-events-auto cursor-pointer',
    'text-base',
    'text-neutral-black hover:text-brand-primary',
    'transition-colors duration-200',
  );

  if (disabled) {
    const disabledClasses = 'cursor-not-allowed opacity-50 hover:text-neutral-black';
    return (
      <span className={cn(linkClassNames, disabledClasses)} title={`${label} (Unavailable)`}>
        {label}
        {icon && <Icon name={icon as TIconName} size={16} />}
      </span>
    );
  }

  return (
    <a
      href={href}
      target={external ? '_blank' : '_self'}
      rel={external ? 'noopener noreferrer' : undefined}
      className={linkClassNames}
    >
      <span className="pointer-events-none">{label}</span>
      {icon && <Icon name={icon as TIconName} size={16} />}
    </a>
  );
};

PopoverLink.displayName = 'PopoverLink';

export default PopoverLink;
